/**
 * Entry point pubblico: reindirizza allo step consigliato (poi navigazione libera).
 */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { fetchOnboardingByToken } from "@/lib/onboarding-public";

const APPLE_FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", system-ui, sans-serif';

export default function OnboardingEntryPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, error, isLoading } = useQuery({
    queryKey: ["onboarding-public", token],
    enabled: !!token,
    queryFn: () => fetchOnboardingByToken(token!),
    retry: false,
  });

  useEffect(() => {
    if (!token || !data) return;
    const step = data.onboarding.current_step;
    const status = data.onboarding.status;
    const ob = data.onboarding;

    // Atterraggio intelligente: non blocca poi la navigazione libera
    if (status === "completato" || step === "done") {
      navigate(`/onboarding/${token}/completato`, { replace: true });
      return;
    }
    if (step === "call" || step === "access") {
      navigate(`/onboarding/${token}/call`, { replace: true });
      return;
    }
    if (step === "payment" || ob.terms_accepted_at) {
      navigate(`/onboarding/${token}/pagamento`, { replace: true });
      return;
    }
    navigate(`/onboarding/${token}/termini`, { replace: true });
  }, [data, navigate, token]);

  if (error) {
    return (
      <div
        className="min-h-[100dvh] flex items-center justify-center px-4"
        style={{
          fontFamily: APPLE_FONT,
          background:
            "radial-gradient(120% 80% at 50% -10%, #e8eef5 0%, #f4f6f8 45%, #eef1f4 100%)",
        }}
      >
        <div className="max-w-sm text-center space-y-2">
          <h1 className="text-lg font-semibold tracking-tight">Link non disponibile</h1>
          <p className="text-sm text-slate-500">
            {error instanceof Error ? error.message : "Errore"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-[100dvh] flex items-center justify-center"
      style={{
        fontFamily: APPLE_FONT,
        background:
          "radial-gradient(120% 80% at 50% -10%, #e8eef5 0%, #f4f6f8 45%, #eef1f4 100%)",
      }}
    >
      {(isLoading || data) && <Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
    </div>
  );
}
